import {GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useEffect} from "react";
import {CheckBox, ColorPickerBox, Column, Row, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {LabelPlacement, ShapeType} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {Rect, Vec2} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {PropertiesUI_SharedProps} from "../PropertiesUI.js";
import {InfoButton, TextPlus} from "web-vcore";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {RectEditor, RectFromPartialRect} from "../../../../Utils/ReactComponents/RectEditor.js";
import {Vec2Editor} from "../../../../Utils/ReactComponents/VecEditor.js";

const splitAt = 70;

export const Visuals_CompUI = observer_mgl((props: PropertiesUI_SharedProps)=>{
	const {map, obj, Change} = props;

	const imageTextInput = React.useRef<TextInput|null>(null);

	useEffect(()=>{
		const listener = (e: ClipboardEvent)=>{
			const imageTextInputEl = imageTextInput.current?.root;
			if (imageTextInputEl == null || document.activeElement != imageTextInputEl) return; // only handle paste if the text-input is focused
			const items = (e.clipboardData ?? e["originalEvent"]?.clipboardData ?? {}).items ?? [];
			for (const item of items) {
				if (item.kind == "file") {
					const blob = item.getAsFile();
					const reader = new FileReader();
					reader.onload = event=>{
						const imageDataStr = event.target!.result as string;
						//Change(a=>a.shape_image_embedded = imageDataStr);
						imageTextInputEl.value = imageDataStr;
					};
					reader.readAsDataURL(blob);
				}
			}
		};
		document.addEventListener("paste", listener);
		return ()=>document.removeEventListener("paste", listener);
	}, [Change]);

	return (
		<Column>
			<Row>
				<CheckBox text="Show label" value={obj.label_show} onChange={val=>Change(a=>a.label_show = val)}/>
				<TextPlus ml={10} info="If label text-override is left blank, the object's name is used.">Text:</TextPlus>
				<TextArea ml={5} mr={10} autoSize={true} style={{flex: 1}} value={obj.label_textOverride ?? ""} onChange={val=>Change(a=>a.label_textOverride = val)}/>
			</Row>
			<Column mt={5} ml={15}>
				<Row>
					<Text>At:</Text>
					<Select ml={5} displayType="button bar" options={GetEntries(LabelPlacement, "ui")} value={obj.label_placement ?? LabelPlacement.center} onChange={val=>Change(a=>a.label_placement = val)}/>
				</Row>
				<Row mt={5}>
					<Text>Offset:</Text>
					<Vec2Editor ml={5} value={obj.label_offset} onChange={val=>Change(a=>a.label_offset = val)}/>
				</Row>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info="If font-height is non-zero, that is used; else, width is used.">Size basis (m):</TextPlus>
					<Text ml={5}>W:</Text>
					<Spinner ml={5} value={obj.label_baseWidth_world} onChange2={val=>Change(a=>a.label_baseWidth_world = val)}/>
					<Text ml={5}>H:</Text>
					<Spinner ml={5} value={obj.label_baseFontHeight_world} onChange2={val=>Change(a=>a.label_baseFontHeight_world = val)}/>
				</Row>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info={`
						These constraints are applied after the base width/height. (ie. constraints are higher priority)
						Also note that the min/max-height is based on the height for a *single line* (not all lines).
					`.AsMultiline(0)}>Constraints (px):</TextPlus>
				</Row>
				<Row mt={5}>
					<Text>W:</Text>
					<Spinner ml={5} value={obj.label_minWidth_screen} onChange2={val=>Change(a=>a.label_minWidth_screen = val)}/>
					<Text ml={5}>-</Text>
					<Spinner ml={5} value={obj.label_maxWidth_screen} onChange2={val=>Change(a=>a.label_maxWidth_screen = val)}/>
					<Text ml={5}>H:</Text>
					<Spinner ml={5} value={obj.label_minHeight_screen} onChange2={val=>Change(a=>a.label_minHeight_screen = val)}/>
					<Text ml={5}>-</Text>
					<Spinner ml={5} value={obj.label_maxHeight_screen} onChange2={val=>Change(a=>a.label_maxHeight_screen = val)}/>
				</Row>
			</Column>
			<Row mt={10}>
				<Text>Shape type:</Text>
				<Select ml={5} displayType="button bar" options={[{name: "None", value: null}, ...GetEntries(ShapeType, "ui")]} value={obj.shape_type} onChange={val=>Change(a=>a.shape_type = val)}/>
			</Row>
			<Column mt={5} ml={15}>
				<Row>
					<Text>Size, X:</Text>
					<Spinner ml={5} value={obj.shape_size?.x} onChange={val=>Change(a=>a.shape_size = new Vec2(val, a.shape_size?.y ?? 0))}/>
					<Text ml={5}>Y:</Text>
					<Spinner ml={5} value={obj.shape_size?.y} onChange={val=>Change(a=>a.shape_size = new Vec2(a.shape_size?.x ?? 0, val))}/>
					{obj.shape_type == ShapeType.rectangle && <>
						<Text ml={10}>Rounding:</Text>
						<Spinner ml={5} style={{width: 50}} placeholder="0" value={obj.shape_rounding} onChange2={val=>Change(a=>a.shape_rounding = val)}/>
						<Text>m</Text>
					</>}
				</Row>
				<Row mt={5}>
					<Text>Outline color:</Text>
					<ColorPickerBox ml={5} color={(obj.shape_outlineColor ?? [255,255,0]) as [number,number,number]} onChange={val=>Change(a=>a.shape_outlineColor = val)}/>
					<Text ml={10}>Opacity:</Text>
					<SpinnerForPercent ml={5} placeholder="100" value={obj.shape_image_opacity} onChange2={val=>Change(a=>a.shape_image_opacity = val)}/>
					<InfoButton ml={5} text={`
						This setting has two effects:
						* The outliner color's opacity is multiplied by this before displaying. (ie. both apply, multicatively)
						* This also sets the opacity for the shape's fill/contents, eg. image and OSM tiles.
					`.AsMultiline(0)}/>
				</Row>
				<Row mt={5}>
					<Text>Image:</Text>
					<TextInput ref={imageTextInput} ml={5} style={{flex: 1}} value={obj.shape_image_embedded ?? ""} onChange={val=>Change(a=>a.shape_image_embedded = val)}/>
				</Row>
				<Row mt={5}>
					<CheckBox text="OSM tiles" value={obj.shape_osm_enabled ?? false} onChange={val=>Change(a=>a.shape_osm_enabled = val)}/>
				</Row>
				{obj.shape_osm_enabled && <Column mt={10} ml={15}>
					<Row>World region (GPS coordinates)</Row>
					<RectEditor mt={5} ml={15} value={RectFromPartialRect(obj.shape_osm_region, true)} onChange={val=>Change(a=>a.shape_osm_region = val)}/>
					<Row mt={5}>
						<TextPlus info={`
							Expands or shrinks the defined region to a certain percent. (eg. for testing tile-load on smaller area first)
							Example: 20% means "leave just the 20% in the center". (ie. cut away 40% from the left, top, right, and bottom sides) 
						`.AsMultiline(0)}>Size adjust:</TextPlus>
						<SpinnerForPercent ml={5} min={0} value={obj.shape_osm_sizeAdjust ?? 1} onChange={val=>Change(a=>a.shape_osm_sizeAdjust = val)}/>
						<CheckBox ml={10} text="Restrict to shape bounds" value={obj.shape_osm_restrictToShapeBounds ?? false} onChange={val=>Change(a=>a.shape_osm_restrictToShapeBounds = val)}/>
						<InfoButton ml={5} text="Note: This doesn't currently take into account the object's/shape's rotation."/>
					</Row>
					<Row mt={5}>
						<Text>Zoom level, base:</Text>
						<Spinner ml={5} min={0} max={20} value={obj.shape_osm_zoom_base ?? 0} onChange={val=>Change(a=>a.shape_osm_zoom_base = val)}/>
						<Text ml={5}>Focus (L1):</Text>
						<Spinner ml={5} min={0} max={20} value={obj.shape_osm_zoom_focusL1 ?? 0} onChange={val=>Change(a=>a.shape_osm_zoom_focusL1 = val)}/>
					</Row>
				</Column>}
			</Column>
		</Column>
	);
});